import type { MotionPreference, ThemeProfile } from './theme-store'
import { deriveSecondaryColors } from './color-utils'
import { isSkin, isThickness, type Skin, type Thickness } from './skin'

/**
 * Applies a ThemeProfile to the document as CSS custom properties.
 * Shared by ThemeInjector (reactive, inside React) and theme-bootstrap.ts
 * (synchronous, before React mounts, so a custom theme never flashes the
 * default one first).
 *
 * Night mode is not a filter any more: the injector passes `nightVariant(p)`
 * (night.ts) here, so the colours themselves change and the semantic ones
 * (ratings, deck colours) survive.
 */
export function applyThemeVariables(profile: ThemeProfile, root: HTMLElement = document.documentElement) {
  const derived = deriveSecondaryColors(profile.colors)
  const set = (name: string, value: string) => root.style.setProperty(name, value)

  // Core palette
  set('--c-bg', profile.colors.bgBase)
  set('--c-surface', profile.colors.surface)
  set('--c-surface-2', derived.surface2)
  set('--c-border', derived.border)
  set('--c-border-dim', derived.borderDim)

  set('--c-green', profile.colors.primary)
  set('--c-green-dim', derived.primaryDim)
  set('--c-green-glow', derived.primaryGlow)
  set('--c-green-text', derived.primaryText)

  set('--c-cream', profile.colors.textTitle)
  set('--c-cream-dim', profile.colors.textBody)
  set('--c-cream-soft', derived.creamSoft)
  set('--c-cream-muted', derived.creamMuted)

  // Semantic text colours, re-checked for AA on this background (ratings,
  // states). On the default theme they equal the stock values.
  set('--c-red-text', derived.redText)
  set('--c-amber', derived.amberText)
  set('--c-blue-text', derived.blueText)
  set('--c-purple-text', derived.purpleText)
  root.setAttribute('data-theme-tone', derived.isLight ? 'light' : 'dark')
  // «Alto contraste» (or a theme made from it): a skin draws its relief as
  // solid borders, with no highlight or texture.
  if (profile.id === 'high-contrast' || profile.base_id === 'high-contrast') root.setAttribute('data-contrast', 'high')
  else root.removeAttribute('data-contrast')
  root.style.colorScheme = derived.isLight ? 'light' : 'dark'
  // Browser chrome (mobile address bar, installed app title bar) follows the page.
  root.ownerDocument.querySelector('meta[name="theme-color"]')?.setAttribute('content', profile.colors.bgBase)

  // Background (SafeBackground paints the layer; these feed it)
  set('--bg-opacity', profile.background.opacity.toString())

  // Typography — mapped onto the variables the rest of the CSS consumes
  set('--font-mono', profile.typography.bodyFont)
  set('--font-display', profile.typography.titleFont)
  set('--font-card', profile.typography.cardFont)
  set('--card-font-size', `${profile.typography.cardFontSize}px`)
  set('--base-font-size', `${profile.typography.baseSize}px`)

  // Effects
  set('--card-radius', `${profile.effects.cardRadius}px`)
  set('--r-xl', `${Math.round(profile.effects.cardRadius * 1.5)}px`)
  set('--card-shadow', profile.effects.cardShadow)
  set('--global-opacity', profile.effects.globalOpacity.toString())

  root.setAttribute('data-quipwa-anim', profile.effects.flipAnimation || 'flip-h')
}

/** `data-night` lets components fine-tune for night (e.g. dim images); colours come from the variant. */
export function applyNightMode(active: boolean, root: HTMLElement = document.documentElement) {
  root.setAttribute('data-night', active ? 'true' : 'false')
}

/**
 * Motion preference → `data-motion` on <html>. tokens.css reads it:
 *   reduced → short durations everywhere; full → ignore the system setting;
 *   system  → no attribute, `prefers-reduced-motion` decides.
 */
export function applyMotion(motion: MotionPreference, root: HTMLElement = document.documentElement) {
  if (motion === 'system') root.removeAttribute('data-motion')
  else root.setAttribute('data-motion', motion)
}

/**
 * Skin and thickness → data-skin and data-thickness on <html>. A skin's
 * rules hang from the attribute (lib/theme/skin.ts), so its stylesheet must be
 * loaded before the attribute names it, or the page shows half a skin. An
 * unknown value (a newer version's skin, a hand-edited store) falls back to
 * the flat skin.
 */
export function applySkin(skin: Skin, thickness: Thickness, root: HTMLElement = document.documentElement) {
  root.setAttribute('data-skin', isSkin(skin) ? skin : 'plana')
  root.setAttribute('data-thickness', isThickness(thickness) ? thickness : 'clasico')
}
